import React, { FC, useEffect, useState } from 'react';
import dayjs from 'dayjs';

import usePrefix from 'utils/usePrefix';
import { ResponseError } from 'utils/api';
import { resqlExpertApi } from 'utils/api/resqlexpert';
import { ResqlExpertSubmissionResponse } from 'utils/api/resqlexpert.d';
import { notificationTypes } from 'utils/constants';
import { useApp } from 'App';
import { ActionTypes } from 'App/types';

import NoData from 'components/NoData';
import Loader from 'components/Loader';
import { CategoryKey, TABS } from 'components/ResqlExpert/constants';
import SubmissionFormContent from 'components/ResqlExpert/SubmissionForm/SubmissionFormContent';
import {
  SubmissionDateStyled,
  SubmissionHeaderDetailStyled,
  SubmissionHeaderStyled,
  SubmissionNameStyled,
  SubmissionStyled,
} from 'components/ResqlExpert/Submission/styles';

import {
  ReadOnlyDualField,
  ReadOnlyListField,
  ReadOnlySingleField,
} from './ReadOnlyTabs';

interface Props {
  id?: number;
}

const Submission: FC<Props> = ({ id }) => {
  const t = usePrefix('ResqlExpert');
  const [, dispatch] = useApp();
  const [detail, setDetail] = useState<ResqlExpertSubmissionResponse | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<CategoryKey>(TABS[0]);

  useEffect(() => {
    if (!id) {
      setDetail(undefined);
      return;
    }
    let cancelled = false;
    resqlExpertApi
      .getSubmissionDetail(id)
      .then((response) => {
        if (!cancelled) setDetail(response);
      })
      .catch((error) => {
        const typedError = error as ResponseError;
        dispatch({
          type: ActionTypes.SET_NOTIFICATION_CODE,
          payload: { code: typedError?.parsed?.code, type: notificationTypes.error },
        });
      });
    return () => {
      cancelled = true;
    };
  }, [id, dispatch]);

  if (!id) return <NoData title={t('no_data')} />;
  if (!detail) return <Loader label={t('loading')} />;

  return (
    <SubmissionStyled>
      <SubmissionHeaderStyled>
        <h2>{detail.title}</h2>
        <SubmissionHeaderDetailStyled>
          <SubmissionNameStyled>
            {t('submitted_by_label', {
              name: `${detail.submitted_by.first_name} ${detail.submitted_by.last_name}`,
              email: detail.submitted_by.email,
            })}
          </SubmissionNameStyled>
          <SubmissionDateStyled>{dayjs(detail.created_at).format('DD-MM-YYYY')}</SubmissionDateStyled>
        </SubmissionHeaderDetailStyled>
      </SubmissionHeaderStyled>
      <SubmissionFormContent
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabMap={{
          PROBLEM: (
            <ReadOnlySingleField
              titleKey="tab_situation"
              hintKey="tab_situation_hint"
              value={detail.situation_description ?? ''}
            />
          ),
          SOURCE: (
            <ReadOnlyListField
              titleKey="tab_sources"
              hintKey="tab_sources_hint"
              values={detail.information_sources ?? []}
            />
          ),
          ACTIONS: (
            <ReadOnlySingleField
              titleKey="tab_actions"
              hintKey="tab_actions_hint"
              value={detail.actions_taken ?? ''}
            />
          ),
          PARTICIPANTS: (
            <ReadOnlyListField
              titleKey="tab_participants"
              hintKey="tab_participants_hint"
              values={detail.involved_parties ?? []}
            />
          ),
          RESULTS: (
            <ReadOnlyDualField
              titleKey="tab_results"
              hintKey="tab_results_hint"
              labelKeyA="tab_results_positive"
              valueA={detail.positive_outcomes ?? ''}
              labelKeyB="tab_results_negative"
              valueB={detail.negative_outcomes ?? ''}
            />
          ),
          RETROSPECTIVE: (
            <ReadOnlyDualField
              titleKey="tab_goals"
              hintKey="tab_goals_hint"
              labelKeyA="tab_goals_short_term"
              valueA={detail.short_term_objectives ?? ''}
              labelKeyB="tab_goals_long_term"
              valueB={detail.long_term_objectives ?? ''}
            />
          ),
        }}
      />
    </SubmissionStyled>
  );
};

export default Submission;
